import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, UploadCloud, CheckCircle2, Briefcase, MapPin, FileText, ShieldCheck, AlertCircle,
  ChevronDown,
} from 'lucide-react';
import { APPLY_FIELDS, APPLY_DOCUMENTS, type ApplyFormConfig } from '../lib/applyForm';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const MAX_FILE_BYTES = 8 * 1024 * 1024; // 8 MB
const MAX_DOC_BYTES = 10 * 1024 * 1024; // 10 MB

interface PublicJob {
  title: string;
  description: string;
  location?: string;
  employment_type?: string;
  language?: string;
  company_name?: string;
  company_logo_url?: string | null;
  application_form_config?: ApplyFormConfig | null;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ApplyPublic: React.FC = () => {
  const { companyId, jobRef } = useParams<{ companyId: string; jobRef: string }>();

  const [job, setJob] = useState<PublicJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [coverLetter, setCoverLetter] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [docFiles, setDocFiles] = useState<Record<string, File>>({});
  const [descOpen, setDescOpen] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/v1/prescreen/public/jobs/${companyId}/${jobRef}`);
        if (resp.ok) {
          const data = await resp.json();
          setJob(data.job);
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      }
      setLoading(false);
    };
    load();
  }, [companyId, jobRef]);

  const isDe = (job?.language || 'de').toLowerCase().startsWith('de');
  const t = (de: string, en: string) => (isDe ? de : en);

  const formConfig: ApplyFormConfig = job?.application_form_config || {};
  const enabledFields = APPLY_FIELDS.filter((f) => (formConfig.fields || {})[f.key]);
  const enabledDocs = APPLY_DOCUMENTS.filter((d) => (formConfig.documents || {})[d.key]);

  const onDocFile = (key: string, f: File | null) => {
    setError('');
    if (!f) {
      setDocFiles((prev) => { const n = { ...prev }; delete n[key]; return n; });
      return;
    }
    const lower = f.name.toLowerCase();
    if (!['.pdf', '.png', '.jpg', '.jpeg'].some((e) => lower.endsWith(e))) {
      setError(t('Bitte PDF, PNG oder JPG hochladen.', 'Please upload a PDF, PNG or JPG.'));
      return;
    }
    if (f.size > MAX_DOC_BYTES) {
      setError(t('Dokument ist zu groß (max. 10 MB).', 'Document too large (max 10 MB).'));
      return;
    }
    setDocFiles((prev) => ({ ...prev, [key]: f }));
  };

  const companyName = job?.company_name?.trim() || '';
  const logoSrc = job?.company_logo_url
    ? `${API_BASE}/api/v1/companies/logos/${job.company_logo_url}`
    : null;

  const BrandHeader = () => {
    if (!companyName && !logoSrc) return null;
    return (
      <div className="mb-6 flex items-center gap-3.5">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={companyName}
            className="h-14 w-14 rounded-2xl border border-slate-200 bg-white object-cover shadow-sm"
          />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-white text-xl font-bold text-slate-700 shadow-sm">
            {companyName.charAt(0).toUpperCase()}
          </div>
        )}
        {companyName && (
          <div>
            <p className="text-lg font-bold leading-tight text-slate-900">{companyName}</p>
            <p className="text-xs text-slate-500">{t('Karriere · Jetzt bewerben', 'Careers · Apply now')}</p>
          </div>
        )}
      </div>
    );
  };

  const PoweredBy = () => (
    <div className="mt-6 text-center text-[11px] text-slate-400">
      <div className="flex items-center justify-center gap-3">
        <a href="/privacy" target="_blank" rel="noreferrer" className="underline-offset-2 hover:text-slate-600 hover:underline">
          {t('Datenschutz', 'Privacy')}
        </a>
        <span aria-hidden className="text-slate-300">·</span>
        <a href="/terms" target="_blank" rel="noreferrer" className="underline-offset-2 hover:text-slate-600 hover:underline">
          {t('AGB', 'Terms')}
        </a>
      </div>
      <p className="mt-2">
        {t('Bewerbungsprozess unterstützt von', 'Hiring process powered by')}{' '}
        <span className="font-semibold text-slate-500">Cevee</span>
      </p>
    </div>
  );

  const onFile = (f: File | null) => {
    setError('');
    if (!f) { setCvFile(null); return; }
    const lower = f.name.toLowerCase();
    if (!lower.endsWith('.pdf') && !lower.endsWith('.txt')) {
      setError(t('Bitte lade eine PDF- oder TXT-Datei hoch.', 'Please upload a PDF or TXT file.'));
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setError(t('Datei ist zu groß (max. 8 MB).', 'File too large (max 8 MB).'));
      return;
    }
    setCvFile(f);
  };

  const submit = async () => {
    setError('');
    if (!name.trim() || !email.trim()) {
      setError(t('Bitte Name und E-Mail angeben.', 'Please enter your name and email.'));
      return;
    }
    if (!cvFile) {
      setError(t('Bitte lade deinen Lebenslauf hoch.', 'Please upload your CV.'));
      return;
    }
    if (!consent) {
      setError(t('Bitte stimme der Verarbeitung deiner Daten zu.', 'Please consent to the processing of your data.'));
      return;
    }
    for (const f of enabledFields) {
      if ((formConfig.fields || {})[f.key] === 'required' && !answers[f.key]?.trim()) {
        setError(t(`Bitte „${f.de}" ausfüllen.`, `Please fill in "${f.en}".`));
        return;
      }
    }
    for (const d of enabledDocs) {
      if ((formConfig.documents || {})[d.key] === 'required' && !docFiles[d.key]) {
        setError(t(`Bitte „${d.de}" hochladen.`, `Please upload "${d.en}".`));
        return;
      }
    }
    setSubmitting(true);
    try {
      const cv_base64 = await fileToBase64(cvFile);
      const form_answers: Record<string, string> = {};
      enabledFields.forEach((f) => {
        const v = answers[f.key]?.trim();
        if (v) form_answers[f.key] = v;
      });
      const documents: { key: string; filename: string; base64: string }[] = [];
      for (const d of enabledDocs) {
        const file = docFiles[d.key];
        if (file) {
          documents.push({ key: d.key, filename: file.name, base64: await fileToBase64(file) });
        }
      }
      const resp = await fetch(`${API_BASE}/api/v1/prescreen/public/apply/${companyId}/${jobRef}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          cover_letter: coverLetter,
          cv_filename: cvFile.name,
          cv_base64,
          consent: true,
          form_answers,
          documents,
        }),
      });
      if (resp.ok) {
        setSubmitted(true);
      } else {
        const data = await resp.json().catch(() => ({}));
        setError(data?.detail || t('Etwas ist schiefgelaufen. Bitte versuche es erneut.', 'Something went wrong. Please try again.'));
      }
    } catch {
      setError(t('Etwas ist schiefgelaufen. Bitte versuche es erneut.', 'Something went wrong. Please try again.'));
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
      </div>
    );
  }

  if (notFound || !job) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <h1 className="text-lg font-bold text-slate-900">Position nicht verfügbar</h1>
          <p className="mt-1 text-sm text-slate-500">
            Diese Stelle existiert nicht mehr oder ist nicht mehr aktiv.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white text-center shadow-sm">
            <div className="h-1.5 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600" />
            <div className="p-8">
              <div className="relative mx-auto mb-4 w-fit">
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt={companyName}
                    className="h-16 w-16 rounded-2xl border border-slate-200 bg-white object-cover shadow-sm"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50">
                    <CheckCircle2 className="h-9 w-9 text-emerald-500" />
                  </div>
                )}
                <span className="absolute -bottom-1.5 -right-1.5 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-emerald-500 shadow-sm">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </span>
              </div>
              <h1 className="text-xl font-bold text-slate-900">{t('Bewerbung eingegangen!', 'Application received!')}</h1>
              <p className="mt-2 text-sm text-slate-500">
                {t(
                  `Danke, ${name.split(' ')[0] || ''}! ${companyName || 'Wir'} ${companyName ? 'hat' : 'haben'} deine Bewerbung für „${job.title}" erhalten und meldet sich bei dir.`,
                  `Thanks, ${name.split(' ')[0] || ''}! ${companyName || 'We'} received your application for "${job.title}" and will be in touch.`,
                )}
              </p>
            </div>
          </div>
          <PoweredBy />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="mx-auto max-w-2xl">
        <BrandHeader />
        {/* Job header */}
        <div className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="h-1.5 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600" />
          <div className="p-6">
            {companyName && (
              <p className="mb-1.5 text-sm font-semibold text-pink-600">
                {t(`Schön, dass du dich bei ${companyName} bewirbst!`, `Great to have you applying at ${companyName}!`)}
              </p>
            )}
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{job.title}</h1>
            <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-500">
              {job.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.location}</span>}
              {job.employment_type && <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {job.employment_type}</span>}
            </div>
            {job.description && (
              <div className="mt-4 rounded-xl border border-slate-200">
                <button
                  type="button"
                  onClick={() => setDescOpen((o) => !o)}
                  className="flex w-full items-center justify-between gap-2 rounded-xl px-4 py-3 text-left transition hover:bg-slate-50"
                >
                  <span className="text-sm font-semibold text-slate-700">{t('Über die Rolle', 'About the role')}</span>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${descOpen ? 'rotate-180' : ''}`} />
                </button>
                {descOpen && (
                  <div className="border-t border-slate-100 px-4 py-3 text-sm leading-relaxed text-slate-600">
                    <p className="whitespace-pre-wrap">{job.description}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold text-slate-900">{t('Deine Bewerbung', 'Your application')}</h2>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="text-xs font-medium text-slate-600">
              {t('Name', 'Name')} *
              <input value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10" />
            </label>
            <label className="text-xs font-medium text-slate-600">
              {t('E-Mail', 'Email')} *
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10" />
            </label>
          </div>

          {/* CV upload */}
          <div className="mt-4">
            <span className="text-xs font-medium text-slate-600">{t('Lebenslauf (PDF / TXT)', 'CV (PDF / TXT)')} *</span>
            <label className="mt-1 flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-4 py-4 hover:border-slate-400">
              <UploadCloud className="h-5 w-5 text-slate-400" />
              <span className="text-sm text-slate-600">
                {cvFile ? (
                  <span className="inline-flex items-center gap-1.5 font-medium text-slate-800"><FileText className="h-4 w-4" /> {cvFile.name}</span>
                ) : t('Datei auswählen oder hierher ziehen', 'Choose a file')}
              </span>
              <input type="file" accept=".pdf,.txt" className="hidden"
                onChange={(e) => onFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          {/* Configurable extra fields: only shown when the recruiter enabled them */}
          {enabledFields.length > 0 && (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {enabledFields.map((f) => {
                const required = (formConfig.fields || {})[f.key] === 'required';
                return (
                  <label key={f.key} className="text-xs font-medium text-slate-600">
                    {isDe ? f.de : f.en} {required && '*'}
                    <input
                      type={f.type === 'number' ? 'number' : 'text'}
                      value={answers[f.key] || ''}
                      onChange={(e) => setAnswers((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder={isDe ? (f.placeholderDe || '') : (f.placeholderEn || '')}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                    />
                  </label>
                );
              })}
            </div>
          )}

          {/* Configurable document uploads */}
          {enabledDocs.length > 0 && (
            <div className="mt-4 space-y-3">
              {enabledDocs.map((d) => {
                const required = (formConfig.documents || {})[d.key] === 'required';
                const file = docFiles[d.key];
                return (
                  <div key={d.key}>
                    <span className="text-xs font-medium text-slate-600">{isDe ? d.de : d.en} {required && '*'}</span>
                    <label className="mt-1 flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-4 py-3 hover:border-slate-400">
                      <UploadCloud className="h-5 w-5 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {file ? (
                          <span className="inline-flex items-center gap-1.5 font-medium text-slate-800"><FileText className="h-4 w-4" /> {file.name}</span>
                        ) : t('PDF, PNG oder JPG', 'PDF, PNG or JPG')}
                      </span>
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
                        onChange={(e) => onDocFile(d.key, e.target.files?.[0] || null)} />
                    </label>
                  </div>
                );
              })}
            </div>
          )}

          <label className="mt-4 block text-xs font-medium text-slate-600">
            {t('Anschreiben (optional)', 'Cover letter (optional)')}
            <textarea value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} rows={5}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-900/10"
              placeholder={t('Warum passt du zu dieser Rolle?', 'Why are you a fit for this role?')} />
          </label>

          {/* Consent */}
          <label className="mt-4 flex items-start gap-2.5 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300" />
            <span className="inline-flex items-start gap-1.5">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              {t(
                'Ich stimme zu, dass meine Bewerbungsunterlagen zur Vorauswahl (inkl. KI-gestützter Analyse) verarbeitet werden. Auf Basis der Analyse kann ich automatisch zu einem (KI-)Interview eingeladen werden. Die finale Entscheidung trifft ein Mensch.',
                'I consent to my application documents being processed for pre-selection (incl. AI-assisted analysis). Based on the analysis I may be automatically invited to an (AI) interview. A human makes the final decision.',
              )}
            </span>
          </label>

          {error && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <button onClick={submit} disabled={submitting}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-pink-600 to-rose-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:from-pink-500 hover:to-rose-500 disabled:opacity-50">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? t('Wird gesendet...', 'Submitting...') : t('Bewerbung absenden', 'Submit application')}
          </button>
        </div>

        <PoweredBy />
      </div>
    </div>
  );
};

export default ApplyPublic;
